import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Typography,
  Button,
  Table,
  Tag,
  message,
  Tooltip,
  DatePicker,
  Empty,
} from "antd";
import { Link, useParams, useNavigate } from "react-router-dom";
import { IntentGroup } from "../../model_gen/intent";
import { Config } from "../../model_gen/config";
import { usePermissions } from "../../PermissionsProvider";
import { IntentService } from "../../services/IntentService";
import moment from "moment";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import {
  DownOutlined,
  RightOutlined,
  CopyOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import { ChatCompletionRequest } from "../../model_gen/chat";
import RequestView from "../shared/RequestView";
import { ColumnsType } from "antd/es/table";
import SentinelList from "../Sentinel/SentinelList";
import { PrimaryButton } from "../shared/button/Buttons";

dayjs.extend(utc);

const IntentGroupOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams<{ intentGroupId: string }>();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [config, setConfig] = useState<Config>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(5);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const requestsByPageRef = useRef<Record<number, any[]>>({});
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [intents, setIntents] = useState<any[]>([]);
  const [intentsLoading, setIntentsLoading] = useState<boolean>(false);
  const [intentsTotal, setIntentsTotal] = useState(0);
  const [intentsCurrentPage, setIntentsCurrentPage] = useState(1);
  const intentsByPageRef = useRef<Record<number, any[]>>({});
  const navigate = useNavigate();

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });

    if (!save || !intentGroup) return;

    IntentService.setIntentGroupConfig(
      Number(intentGroupId),
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setConfig(updatedConfig);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const resetConfig = () => {
    if (!intentGroup) return;

    IntentService.resetIntentGroupConfig(
      Number(intentGroupId),
      null,
      apiKey
    ).subscribe(
      () => {
        setConfig(undefined);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const fetchRequests = (page: number) => {
    if (requestsByPageRef.current[page] && !startDate && !endDate) {
      setRequests(requestsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * requestPageSize;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    IntentService.getIntentGroupRequests(
      Number(intentGroupId),
      start,
      end,
      offset,
      requestPageSize,
      setRequestsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        requestsByPageRef.current[page] = data.items || [];
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
      }
    );
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const fetchIntents = (page: number) => {
    if (intentsByPageRef.current[page]) {
      setIntents(intentsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * 10;
    setIntentsLoading(true);

    IntentService.getIntentsByIntentGroup(
      Number(intentGroupId),
      offset,
      10,
      setIntentsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setIntents(data.items || []);
        setIntentsTotal(data.total || 0);
        intentsByPageRef.current[page] = data.items || [];
        setIntentsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIntentsLoading(false);
      }
    );
  };

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        true,
        setIsLoading,
        apiKey
      ).subscribe(
        (group: IntentGroup) => {
          setIntentGroup(group);
          setConfig(group.config);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (intentGroupId && expandedPanels.has("requests")) {
      if (!requestsByPageRef.current[requestsCurrentPage]) {
        fetchRequests(requestsCurrentPage);
      } else {
        setRequests(requestsByPageRef.current[requestsCurrentPage]);
      }
    }
  }, [intentGroupId, requestsCurrentPage, expandedPanels, requestPageSize]);

  useEffect(() => {
    if ((!startDate || startDate == null) && (!endDate || endDate == null)) {
      requestsByPageRef.current = {};
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (intentGroupId && expandedPanels.has("intents")) {
      if (!intentsByPageRef.current[intentsCurrentPage]) {
        fetchIntents(intentsCurrentPage);
      } else {
        setIntents(intentsByPageRef.current[intentsCurrentPage]);
      }
    }
  }, [intentGroupId, intentsCurrentPage, expandedPanels]);

  const togglePanel = (panel: string, checked: boolean) => {
    const newSet = new Set(expandedPanels);
    if (checked) {
      newSet.add(panel);
      if (
        panel === "requests" &&
        !requestsByPageRef.current[requestsCurrentPage]
      ) {
        fetchRequests(requestsCurrentPage);
      }
    } else {
      newSet.delete(panel);
    }
    setExpandedPanels(newSet);
  };

  const intentsColumns = [
    {
      title: "Application",
      dataIndex: ["meta", "metadata", "application_name"],
      key: "application_name",
      render: (text: string, record: any) => (
        <Link to={`/applications/${record.application_id}`}>{text}</Link>
      ),
    },
    {
      title: "Intent",
      dataIndex: "action_type",
      key: "action_type",
    },
    {
      title: "Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (timestamp: number) =>
        moment(timestamp).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity",
      key: "last_activity",
      render: (timestamp: number) =>
        moment(timestamp).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Requests",
      dataIndex: "request_count",
      key: "request_count",
    },
  ];

  const requestColumns: ColumnsType<
    ChatCompletionRequest & { application_id?: number }
  > = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Application",
      key: "application_name",
      width: "15%",
      render: (_, request: ChatCompletionRequest) =>
        request.metadata?.application_name || "-",
    },
    {
      title: "Intent",
      key: "intent_name",
      width: "15%",
      render: (_, request: ChatCompletionRequest) =>
        request.metadata?.intent_name || "-",
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Space wrap>
              {tags.map((tag: string) => (
                <Tag key={tag} bordered={true}>
                  {tag}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "20%",
      align: "right",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <span>...{requestId.slice(-8)}</span>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_, record: ChatCompletionRequest) => (
        <Link
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}
          target="_blank">
          <Tooltip
            title={<RequestView request={record} apiKey={apiKey} />}
            placement="left"
            overlayStyle={{ maxWidth: "600px", width: "600px" }}>
            <Button type="link" icon={<EyeOutlined />}>
              View
            </Button>
          </Tooltip>
        </Link>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      />
    );
  }

  return (
    <Card className="applications-container applications-wrapper">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
            <Link to="/intent-groups" style={{ color: "#828282" }}>
              Intent Groups {" / "}{" "}
            </Link>
            {intentGroup?.intent_name}
          </Typography>
        </Col>
        <Col>
          <Link to={`/intent-group/${intentGroupId}/testsets`}>
            <Button type="primary">View Test Sets</Button>
          </Link>
        </Col>
      </Row>
      <Card className="overview-wrapper" style={{ marginBottom: 24 }} bordered>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Descriptions colon={false} column={12} layout="vertical">
              <Descriptions.Item label="Group Name" span={3}>
                {intentGroup?.intent_name}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div style={{ display: "flex", alignItems: "start" }}>
                    Tags
                    <AiStars
                      style={{
                        width: "12px",
                        height: "12px",
                        color: "#8c8c8c",
                      }}
                    />
                  </div>
                }
                span={3}>
                {(() => {
                  try {
                    const tags = JSON.parse(
                      (intentGroup?.metadata?.tags as string) || "[]"
                    );
                    return (
                      <Space wrap style={{ whiteSpace: "pre-wrap" }}>
                        {tags.map((tag: string) => (
                          <Tag key={tag} bordered={true}>
                            {tag}
                          </Tag>
                        ))}
                      </Space>
                    );
                  } catch (e) {
                    return "-";
                  }
                })()}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div style={{ display: "flex", alignItems: "start" }}>
                    Description
                    <AiStars
                      style={{
                        width: "12px",
                        height: "12px",
                        color: "#8c8c8c",
                      }}
                    />
                  </div>
                }
                span={6}>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {(() => {
                    try {
                      return (intentGroup?.metadata?.intent as string) || "-";
                    } catch (e) {
                      return "-";
                    }
                  })()}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={24}>
            <Descriptions colon={false} column={12} layout="vertical">
              <Descriptions.Item label="Notifications" span={3}>
                {intentGroup?.metadata?.notifications_enabled
                  ? "Enabled"
                  : "Disabled"}
              </Descriptions.Item>
              <Descriptions.Item label="Last Activity" span={3}>
                {intentGroup?.last_activity
                  ? moment(intentGroup?.last_activity).format(
                      "YYYY-MM-DD hh:mm:ss A"
                    )
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Requests" span={6}>
                {intentGroup?.request_count}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>
      <Card className="overview-wrapper-panel" bordered>
        <ConfigurationPanel
          intentGroupId={Number(intentGroupId)}
          config={config}
          updateConfig={updateConfig}
          resetConfig={resetConfig}
        />
      </Card>
      {intentGroup && (
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", marginTop: "24px" }}>
          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: "8px" }}>
            <Row align="middle">
              <Col flex="none" style={{ marginRight: "8px" }}>
                <Button
                  type="text"
                  icon={
                    expandedPanels.has("intents") ? (
                      <DownOutlined />
                    ) : (
                      <RightOutlined />
                    )
                  }
                  onClick={() =>
                    togglePanel("intents", !expandedPanels.has("intents"))
                  }
                />
              </Col>
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Intents
                </Typography.Title>
              </Col>
            </Row>
          </Row>
          {expandedPanels.has("intents") && (
            <Table
              dataSource={intents}
              columns={intentsColumns}
              loading={intentsLoading}
              rowKey="id"
              pagination={{
                current: intentsCurrentPage,
                pageSize: 10,
                total: intentsTotal,
                onChange: (page) => setIntentsCurrentPage(page),
              }}
              onRow={(record) => ({
                onClick: () =>
                  navigate(
                    `/application/${record.application_id}/intent/${record.id}`
                  ),
                style: { cursor: "pointer" },
              })}
            />
          )}

          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: "8px", marginTop: "16px" }}>
            <Row align="middle">
              <Col flex="none" style={{ marginRight: "8px" }}>
                <Button
                  type="text"
                  icon={
                    expandedPanels.has("requests") ? (
                      <DownOutlined />
                    ) : (
                      <RightOutlined />
                    )
                  }
                  onClick={() =>
                    togglePanel("requests", !expandedPanels.has("requests"))
                  }
                />
              </Col>
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Requests
                </Typography.Title>
              </Col>
            </Row>
          </Row>
          {expandedPanels.has("requests") && (
            <>
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 16 }}>
                <Col>
                  <Space>
                    <DatePicker
                      placeholder="Start Date"
                      onChange={(date: Dayjs | null) => {
                        setStartDate(date);
                      }}
                      value={startDate}
                      disabledDate={(current: Dayjs) =>
                        endDate ? current && current > endDate : false
                      }
                    />
                    <DatePicker
                      placeholder="End Date"
                      onChange={(date: Dayjs | null) => {
                        setEndDate(date);
                      }}
                      value={endDate}
                      disabledDate={(current: Dayjs) =>
                        startDate ? current && current < startDate : false
                      }
                    />
                    <Button
                      onClick={handleClearFilters}
                      disabled={!endDate && !startDate}>
                      Clear Filters
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        requestsByPageRef.current = {};
                        setRequestsCurrentPage(1);
                        fetchRequests(1);
                      }}
                      disabled={!startDate && !endDate}>
                      Search
                    </Button>
                  </Space>
                </Col>
              </Row>
              <Table
                rowKey="request_id"
                columns={requestColumns}
                dataSource={requests}
                loading={requestsLoading}
                pagination={{
                  current: requestsCurrentPage,
                  pageSize: requestPageSize,
                  total: requestsTotal,
                  onChange: (page, pageSize) => {
                    setRequestsCurrentPage(page);
                    if (pageSize && pageSize !== requestPageSize) {
                      setRequestPageSize(pageSize);
                      requestsByPageRef.current = {};
                    }
                  },
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  onShowSizeChange: (current, size) => {
                    setRequestsCurrentPage(1);
                    setRequestPageSize(size);
                    requestsByPageRef.current = {};
                  },
                }}
              />
            </>
          )}

          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: "8px", marginTop: "16px" }}>
            <Row align="middle">
              <Col flex="none" style={{ marginRight: "8px" }}>
                <Button
                  type="text"
                  icon={
                    expandedPanels.has("sentinels") ? (
                      <DownOutlined />
                    ) : (
                      <RightOutlined />
                    )
                  }
                  onClick={() =>
                    togglePanel("sentinels", !expandedPanels.has("sentinels"))
                  }
                />
              </Col>
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Sentinels
                </Typography.Title>
              </Col>
            </Row>
            <Col>
              <PrimaryButton
                title="New Sentinel"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate(`/intent-group/${intentGroupId}/sentinel/new`)
                }
              />
            </Col>
          </Row>
          {expandedPanels.has("sentinels") && (
            <SentinelList
              intentGroupId={Number(intentGroupId)}
              emptyText={
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No sentinels found"
                />
              }
              excludeHeader={true}
            />
          )}
        </Space>
      )}
    </Card>
  );
};

export default IntentGroupOverview;
